import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'

import RegistrationCard from '../registration/RegistrationCard'
import { EXP_REGEX, LIONS_CLUB_CAP } from '../constants'
import LionsClubCard, { LionsClubCardContent } from './LionsClubCard'
import LegalAgreement from './LegalAgreement'
import useIsMobile from '../hooks/useIsMobile'
import BillingInfo from './BillingInfo'
import DemographicInfo from './DemographicInfo'
import Claim from '../claim/Claim'

const Row = ({ children }) => {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', mr: -1 }}>{children}</Box>
  )
}

const Register = () => {
  const [contactValues, setContactValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    title: '',
  })

  const [billingInfo, setBillingInfo] = useState({
    cardNumber: '',
    cardName: '',
    exp: '',
    streetAddress: '',
    city: '',
    state: '',
    postal: '',
  })
  const navigate = useNavigate()

  const isMobile = useIsMobile()

  const [submitError, setSubmitError] = useState(false)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const [page, setPage] = useState('REGISTRATION')

  const [createdContact, setCreatedContact] = useState(null)

  const [cardIsTallerThanBody, setCardIsTallerThanBody] = useState(false)

  const [errorCode, setErrorCode] = useState(null)

  const [lionsClubCount, setLionsClubCount] = useState(null)

  const [priceData, setPriceData] = useState({})

  const getLionsClub = () => {
    return axios.get('/api/registration/lions-club').then((res) => {
      setLionsClubCount(res.data.count)
      setPriceData(res.data)
    })
  }

  useEffect(() => {
    getLionsClub()
  }, [])

  const wouldBeInLionsClub = lionsClubCount && lionsClubCount <= LIONS_CLUB_CAP

  const clearErrors = () => {
    setSubmitError(false)
    setErrorCode(null)
  }

  const handleContinueButton = () => {
    setSubmitting(true)

    return axios
      .post('/api/registration', {
        contact: contactValues,
        billing: billingInfo,
      })
      .then((res) => {
        setCreatedContact(res.data.contact)
        setPage('CLAIM')
      })
      .catch((err) => {
        if (err.response.data.code === 'DUPLICATE_EMAIL') {
          setErrorCode('DUPLICATE_EMAIL')
        }
        if (err.response.data.code === 'BILLING') {
          setErrorCode('BILLING')
        }
        setSubmitError(true)
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const handleContactFieldChange = (newContactValues) => {
    clearErrors()
    setContactValues(newContactValues)
  }

  const handleBillingFieldChange = (newBillingValues) => {
    setBillingInfo(newBillingValues)
  }

  const navigateBackToLogin = () => navigate('/login')

  const disabled =
    Object.keys(contactValues).some((key) => contactValues[key] === '') ||
    Object.keys(billingInfo).some((key) => billingInfo[key] === '')

  useEffect(() => {
    setCardIsTallerThanBody(
      document.body.offsetHeight <
        document.getElementById('registration-card')?.offsetHeight
    )
  }, [submitError, submitSuccess, submitting])

  const today = new Date()
  const startDate = new Date(today.setDate(today.getDate() + 31))

  const price = wouldBeInLionsClub
    ? priceData.LIONS_CLUB_PRICE
    : priceData.DEFAULT_PRICE

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: '#9ac4e7',
        minHeight: '100vh',
        py: isMobile ? null : 2,
      }}
    >
      <Box
        sx={{
          ...(wouldBeInLionsClub
            ? { display: 'flex', flexDirection: 'column' }
            : {}),
        }}
      >
        {page === 'CLAIM' && createdContact?.oneTimeCode && (
          <>
            <Claim
              claimOverride={createdContact.oneTimeCode}
              lionsClub={
                <LionsClubCardContent count={lionsClubCount} isMobile />
              }
              contactOverride
              text={
                <Box
                  sx={{
                    maxWidth: '400px',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    textAlign: 'center',
                  }}
                >
                  <Typography sx={{ mb: 1 }}>
                    You have not been charged yet. Please finish creating your
                    account below to begin your free trial
                  </Typography>
                  <Typography>
                    You will be billed <b>${price}</b> monthly starting on{' '}
                    <b>{startDate.toLocaleDateString()}</b>
                  </Typography>
                </Box>
              }
            />
          </>
        )}

        {page === 'REGISTRATION' && wouldBeInLionsClub && !isMobile && (
          <LionsClubCard count={lionsClubCount} />
        )}

        {page === 'REGISTRATION' && (
          <RegistrationCard
            cardIsTallerThanBody={cardIsTallerThanBody}
            title={submitting ? '' : 'Registration'}
          >
            {wouldBeInLionsClub && isMobile && (
              <LionsClubCardContent count={lionsClubCount} isMobile />
            )}

            {submitting && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                <CircularProgress sx={{ mb: 2 }} />
                <span>Provisioning Account</span>
              </Box>
            )}
            {!submitting && submitSuccess && (
              <div>
                <Alert severity='success'>
                  <b>Success!</b> Check your email with instructions on how to
                  claim your account
                </Alert>
                <div className='success-card-buttons'>
                  <Button variant='outlined' onClick={navigateBackToLogin}>
                    Back to Login
                  </Button>
                </div>
              </div>
            )}

            {!submitting && !submitSuccess && (
              <>
                {submitError && (
                  <Alert severity='error' sx={{ mt: '8px' }}>
                    {errorCode === 'DUPLICATE_EMAIL' && (
                      <>The email address provided is already in use</>
                    )}
                    {errorCode === 'BILLING' && (
                      <>
                        There was an error during billing. Please check your
                        information.
                      </>
                    )}
                    {!errorCode && (
                      <>
                        There was an error during registration. Please double
                        check your information and try again.
                      </>
                    )}
                  </Alert>
                )}

                {/* {page === 'LEGAL_AGREEMENT' && (
                <LegalAgreement
                  onSubmit={handleRegister}
                  disabled={disabled}
                  goBack={() => setPage('CONTACT_INFO')}
                />
              )} */}

                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '500px',
                        display: 'flex',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      <Typography>
                        Continue filling in your information to start your
                        StartUP CRM 30 day free trial. After the free trial, You
                        will be billed <b>${price}</b> monthly starting on{' '}
                        <b>{startDate.toLocaleDateString()}</b>.
                      </Typography>
                      <Typography>
                        Your subscription will not begin until continuing on the
                        next page.
                      </Typography>
                    </Box>
                  </Box>
                  <DemographicInfo
                    contactValues={contactValues}
                    handleContactFieldChange={handleContactFieldChange}
                    Row={Row}
                  />
                </>

                <>
                  <BillingInfo
                    billingInfo={billingInfo}
                    handleBillingFieldChange={handleBillingFieldChange}
                    Row={Row}
                  />
                </>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: '16px',
                  }}
                >
                  <Button onClick={() => navigate('/login')}>Cancel</Button>

                  <Button
                    sx={{ marginLeft: '8px' }}
                    onClick={handleContinueButton}
                    variant={'contained'}
                    disabled={disabled}
                  >
                    Next
                  </Button>
                </Box>
              </>
            )}
          </RegistrationCard>
        )}
      </Box>
    </Box>
  )
}

export default Register
